/**
 * RealEstateCard (Patch BL): list of real-estate properties with
 * optional per-property mortgage modeling.
 *
 * Balance-sheet approach: full property market value is the Asset,
 * outstanding mortgage balance is the Liability. Net worth combines
 * both correctly (Assets - Liabilities = Equity). The Dashboard pies
 * (Patch BM) show home at full value in Assets and mortgage at
 * outstanding balance in Liabilities.
 *
 * Mortgage inputs are over-determined: any three of (balance, monthly
 * payment, APR, end date) determine the fourth. We accept all four
 * (because the user usually knows all four from their statement) and
 * surface a small "implied end date" hint when they diverge. We don't
 * auto-correct either value.
 *
 * balanceAsOfDate anchors the user's last-known balance. Current balance
 * at render time is computed by rolling forward through monthly
 * amortization (engine/mortgage.ts). Same compute-on-render pattern
 * computeLoanState uses for the Bitcoin loan.
 */

import { useState } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { DecimalInput } from './DecimalInput.js';
import { currentMortgageBalance, impliedMonthsToPayoff, addMonths } from '../engine/mortgage.js';
import type { RealEstateProperty, RealEstatePropertyType, Mortgage } from '../engine/types.js';

const PROPERTY_TYPE_LABELS: Record<RealEstatePropertyType, string> = {
  primary_home: 'Primary home',
  other_home: 'Other home',
  multi_family: 'Multi-family',
  investment: 'Investment property',
  commercial: 'Commercial',
  land: 'Land',
  other: 'Other',
};

const PROPERTY_TYPES: RealEstatePropertyType[] = [
  'primary_home', 'other_home', 'multi_family', 'investment', 'commercial', 'land', 'other',
];

/** Default fresh mortgage shape when the user toggles "has mortgage" on
 *  a property that currently has none. balanceAsOfDate defaults to today
 *  so future amortization is anchored correctly. */
function defaultMortgage(): Mortgage {
  const today = new Date();
  return {
    outstandingBalanceUsd: new Decimal(0),
    balanceAsOfDate: today,
    monthlyPaymentUsd: new Decimal(0),
    aprPct: new Decimal('0.06'),
  };
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parseIsoDate(s: string): Date {
  return new Date(s + 'T12:00:00Z');
}

export function RealEstateCard(): JSX.Element {
  const properties = useAppStore(s => s.realEstateProperties);
  const addProperty = useAppStore(s => s.addRealEstateProperty);
  const updateProperty = useAppStore(s => s.updateRealEstateProperty);
  const removeProperty = useAppStore(s => s.removeRealEstateProperty);

  return (
    <div>
      <p className="muted" style={{ fontSize: 'var(--fs-small)', lineHeight: 1.6, marginTop: 0, marginBottom: 'var(--gap-m)' }}>
        Each property is tracked at full market value as an Asset. Any
        attached mortgage&apos;s outstanding balance becomes a Liability,
        rolled forward automatically using the amortization terms you
        enter. Standard balance-sheet view, so the Dashboard breaks out
        both sides clearly.
      </p>

      {properties.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', marginBottom: 'var(--gap-m)' }}>
          No properties yet. Add one below.
        </p>
      ) : (
        properties.map(p => (
          <PropertyBlock
            key={p.id}
            property={p}
            onUpdate={patch => updateProperty(p.id, patch)}
            onRemove={() => {
              if (confirm(`Remove ${p.label || 'this property'}? Undoable from the header Undo button.`)) {
                removeProperty(p.id);
              }
            }}
          />
        ))
      )}

      <div style={{
        marginTop: properties.length > 0 ? 'var(--gap-l)' : 'var(--gap-m)',
        paddingTop: 'var(--gap-m)',
        borderTop: properties.length > 0 ? '1px dashed var(--rule)' : 'none',
      }}>
        <button
          onClick={() => addProperty({
            label: 'New property',
            propertyType: 'primary_home',
            currentValueUsd: new Decimal(0),
            mortgage: null,
          })}
          style={{ fontSize: 'var(--fs-small)' }}
        >
          + Add property
        </button>
      </div>
    </div>
  );
}

// ---------- PropertyBlock: one property's editable form ----------

interface PropertyBlockProps {
  property: RealEstateProperty;
  onUpdate: (patch: Partial<RealEstateProperty>) => void;
  onRemove: () => void;
}

function PropertyBlock(props: PropertyBlockProps): JSX.Element {
  const { property, onUpdate, onRemove } = props;
  const m = property.mortgage;

  // Compute the current mortgage balance at render time. This is what
  // feeds the Dashboard Liabilities pie + the equity tile below.
  const currentBalance = m ? currentMortgageBalance(m, new Date()) : null;
  const equity = currentBalance !== null
    ? property.currentValueUsd.minus(currentBalance)
    : property.currentValueUsd;

  // Implied end date from balance + payment + APR. Patch BP: no longer
  // compared to a user-entered endDate (which was removed); just
  // computed for display under the mortgage inputs so the user can see
  // when their current numbers imply the loan will be paid off.
  let impliedEndDate: Date | null = null;
  if (m && currentBalance !== null && currentBalance.greaterThan(0)) {
    const months = impliedMonthsToPayoff(currentBalance, m.monthlyPaymentUsd, m.aprPct);
    if (months !== null && isFinite(months)) {
      impliedEndDate = addMonths(new Date(), Math.round(months));
    }
  }

  function toggleMortgage(checked: boolean): void {
    onUpdate({ mortgage: checked ? defaultMortgage() : null });
  }

  function updateMortgage(patch: Partial<Mortgage>): void {
    if (!m) return;
    onUpdate({ mortgage: { ...m, ...patch } });
  }

  return (
    <div style={{
      marginBottom: 'var(--gap-l)',
      paddingTop: 'var(--gap-m)',
      borderTop: '1px dashed var(--rule)',
    }}>
      {/* Header: label + type + value + remove */}
      <div className="row" style={{ alignItems: 'center', gap: 'var(--gap-m)', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={property.label}
          onChange={e => onUpdate({ label: e.target.value })}
          placeholder="Property name"
          style={{ width: 200, fontWeight: 600, fontSize: 'var(--fs-base)' }}
        />
        <select
          value={property.propertyType}
          onChange={e => onUpdate({ propertyType: e.target.value as RealEstatePropertyType })}
          style={{ fontSize: 'var(--fs-small)' }}
        >
          {PROPERTY_TYPES.map(t => (
            <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
          <span className="faint">value $</span>
          <DecimalInput
            decimals={0}
            step="10000"
            value={property.currentValueUsd}
            onChange={d => onUpdate({ currentValueUsd: d })}
            style={{ width: 130 }}
          />
        </div>
        <button
          className="ghost"
          onClick={onRemove}
          style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
          title="Remove this property"
        >
          remove
        </button>
      </div>

      {/* Mortgage toggle + fields when enabled. */}
      <div style={{ marginTop: 'var(--gap-s)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-small)' }}>
          <input
            type="checkbox"
            checked={m !== null}
            onChange={e => toggleMortgage(e.target.checked)}
          />
          <span>Has mortgage</span>
        </label>

        {m ? (
          <div style={{
            marginTop: 'var(--gap-s)',
            padding: 'var(--gap-m)',
            background: 'var(--bg-elev)',
            border: '1px solid var(--rule)',
            borderRadius: 6,
          }}>
            <div className="row" style={{ gap: 'var(--gap-m)', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
                <span className="faint">balance $</span>
                <DecimalInput
                  decimals={0}
                  step="1000"
                  value={m.outstandingBalanceUsd}
                  onChange={d => updateMortgage({ outstandingBalanceUsd: d })}
                  style={{ width: 130 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
                <span className="faint">as of</span>
                <input
                  type="date"
                  value={isoDate(m.balanceAsOfDate)}
                  onChange={e => { if (e.target.value) updateMortgage({ balanceAsOfDate: parseIsoDate(e.target.value) }); }}
                  style={{ fontSize: 'var(--fs-small)' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
                <span className="faint">monthly payment $</span>
                <DecimalInput
                  decimals={2}
                  step="50"
                  value={m.monthlyPaymentUsd}
                  onChange={d => updateMortgage({ monthlyPaymentUsd: d })}
                  style={{ width: 100 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)' }}>
                <span className="faint">APR</span>
                <DecimalInput
                  decimals={3}
                  step="0.125"
                  value={m.aprPct.times(100)}
                  onChange={d => updateMortgage({ aprPct: d.div(100) })}
                  style={{ width: 70 }}
                />
                <span className="faint">%</span>
              </div>
              {/* Patch BP: endDate input removed - over-determined with
                  the other three fields and confused users. Implied
                  payoff date is shown below in the computed row. */}
            </div>

            {/* Computed: current balance, equity, implied end-date display. */}
            <div className="row" style={{ marginTop: 'var(--gap-s)', gap: 'var(--gap-m)', flexWrap: 'wrap', fontSize: 'var(--fs-small)' }}>
              <span className="mono">
                <span className="faint">current balance: </span>
                {currentBalance !== null
                  ? <span style={{ color: 'var(--neg)' }}>${formatUsd(currentBalance, { decimals: 0 })}</span>
                  : '-'}
              </span>
              <span className="mono">
                <span className="faint">equity: </span>
                <span style={{ color: 'var(--accent)' }}>${formatUsd(equity, { decimals: 0 })}</span>
              </span>
              {impliedEndDate ? (
                <span className="mono">
                  <span className="faint">payoff by: </span>
                  {isoDate(impliedEndDate)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
